import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowRight, Loader2, Mail, Shield, Sparkles, User, Video } from 'lucide-react'
import { Button } from '@/clientWeb/components/ui/button'
import { Input } from '@/clientWeb/components/ui/input'
import { cn } from '@/clientWeb/components/lib/utils'
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'
import { selectIsAuthenticated, useAuthStore } from '@/features/auth/store/auth.store'
import { exitMeetingRoute } from '@/features/embed/meeting-exit'

const JOIN_DELAY_MS = 3000

interface ReadyToJoinPanelProps {
  showReadyToJoin: boolean
  userName: string
  pendingCode: string
  onUserNameChange: (name: string) => void
  onJoin: () => void
  isWebinar?: boolean
  /**
   * Every seat is taken. Stops the spinner the Join click started and keeps
   * the button disabled: the click's own state never learns the join was
   * refused, so it would otherwise spin behind the "meeting is full" dialog,
   * and on after it was closed.
   */
  joinBlocked?: boolean
}

export function ReadyToJoinPanel({
  showReadyToJoin,
  userName,
  onUserNameChange,
  onJoin,
  isWebinar,
  joinBlocked = false,
}: ReadyToJoinPanelProps) {
  const t = useTranslation()
  const navigate = useNavigate()

  // A signed-in user is already identified. Asking them to retype an address we
  // hold — and could not let them change anyway, since it keys their account —
  // is friction with nothing behind it, so the field is not rendered at all.
  // Guests still supply one.
  // `user` is hydrated from localStorage and outlives the session, so `!!user`
  // called a guest signed-in: it hid the email field they are required to give
  // and seeded the form with the last account used on this browser. Gate on the
  // real session predicate. `?role=guest` used to override it as well; that
  // flag is gone, so a real session is the whole test.
  const authUser = useAuthStore((s) => s.user)
  const isAuthenticated = useAuthStore(selectIsAuthenticated)
  const isSignedIn = isAuthenticated

  const [emailId, setEmailId] = useState(isSignedIn ? authUser?.email ?? '' : '')
  const [isJoining, setIsJoining] = useState(false)
  const [nameError, setNameError] = useState('')
  const [emailError, setEmailError] = useState('')

  useEffect(() => {
    if (joinBlocked) setIsJoining(false)
  }, [joinBlocked])

  const handleJoinClick = () => {
    if (joinBlocked) return
    const trimmedName = userName.trim()
    const trimmedEmail = emailId.trim()
    let hasError = false

    // Only the person who can still type is asked to get it right. The field
    // is disabled when signed in, so a short account name would otherwise be an
    // error the user is given no way to clear — the same reason the email check
    // below is skipped for them.
    if (!isSignedIn && trimmedName.length < 2) {
      setNameError(t('dashboard.enterFullName'))
      hasError = true
    } else {
      setNameError('')
    }

    if (isSignedIn) {
      setEmailError('')
    } else if (!trimmedEmail) {
      setEmailError(t('dashboard.emailPlaceholder'))
      hasError = true
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setEmailError(t('dashboard.invalidEmail'))
      hasError = true
    } else {
      setEmailError('')
    }

    if (hasError) return

    setIsJoining(true)
    setTimeout(() => {
      onJoin()
    }, JOIN_DELAY_MS)
  }

  return (
    <div
      className={cn(
        'transition-all duration-500 ease-out',
        showReadyToJoin
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 translate-y-4 pointer-events-none absolute inset-0',
      )}
    >
      <div className="relative">
        <div className="relative backdrop-blur-xl rounded-3xl overflow-hidden">
          <div className="p-4 sm:p-8">
            <div className="text-center mb-4 sm:mb-8">
              <div className="relative hidden wide-landscape:flex justify-center items-center mb-4">
                <div className="absolute inset-0 bg-primary/5 rounded-2xl blur-xl animate-pulse" />
                <div className="relative w-16 h-16 bg-gradient-to-br from-primary to-primary/80 rounded-2xl flex items-center justify-center shadow-lg shadow-primary/25">
                  <Video className="w-8 h-8 text-primary-foreground" />
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-secondary rounded-full flex items-center justify-center shadow-md">
                    <Sparkles className="w-3 h-3 text-secondary-foreground" />
                  </div>
                </div>
              </div>

              <h1 className="text-md sm:text-3xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent mb-1 sm:mb-2">
                {t('dashboard.readyToJoin')}
              </h1>
            </div>

            <div className="space-y-5">
              <div className="relative group">
                {/* Not editable once signed in — the account names you.
                    It used to take anything typed here and then ignore it: only
                    the guest path records the typed name, so a signed-in user
                    who renamed themselves still joined as their account. Worse
                    than cosmetic to "fix" the other way round, too — remote
                    tiles read the name out of Presence, which comes from the
                    room token the backend mints from the account, so honouring
                    it on this screen alone would show the new name to its owner
                    and the old one to everybody else.

                    Same rule the email field below already follows: what the
                    account knows, the account supplies. */}
                <Input
                  id="join-name"
                  value={userName}
                  variant="outlined"
                  disabled={isSignedIn}
                  onChange={(e) => { onUserNameChange(e.target.value.slice(0, 50)); if (nameError) setNameError('') }}
                  placeholder={t('dashboard.enterFullName')}
                  className={cn('dark:text-white', nameError && 'border-destructive focus-visible:ring-destructive')}
                  startIcon={<User className="w-4 h-4" />}
                  maxLength={50}
                />
                {nameError && <p className="mt-1 text-xs text-destructive">{nameError}</p>}
              </div>

              {!isSignedIn && (
                <div className="relative group">
                  <Input
                    id="join-email"
                    value={emailId}
                    onChange={(e) => { setEmailId(e.target.value.slice(0, 254)); if (emailError) setEmailError('') }}
                    placeholder={t('dashboard.emailPlaceholder')}
                    className={cn('dark:text-white', emailError && 'border-destructive focus-visible:ring-destructive')}
                    startIcon={<Mail className="w-4 h-4" />}
                    type="email"
                    maxLength={254}
                  />
                  {emailError && <p className="mt-1 text-xs text-destructive">{emailError}</p>}
                </div>
              )}

              <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground/80 bg-muted/50 rounded-full py-2 px-4 mx-auto w-fit">
                <Shield className="w-3.5 h-3.5 text-green-500" />
                <span>{t('dashboard.endToEndEncrypted')}</span>
              </div>
            </div>

            <div className="mt-4 space-y-3">
              <Button
                variant="default"
                onClick={handleJoinClick}
                disabled={isJoining || joinBlocked}
                className={cn(
                  'w-full h-14 text-base font-semibold rounded-xl transition-all duration-300',
                  'bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary',
                  'shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30',
                  'disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none',
                  'group flex items-center justify-center gap-2',
                )}
              >
                {isJoining ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>{t('dashboard.joining')}</span>
                  </>
                ) : (
                  <>
                    <span>{isWebinar ? t('dashboard.joinWebinar') : t('dashboard.joinMeeting')}</span>
                    <ArrowRight className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
                  </>
                )}
              </Button>

              <Button
                variant="ghost"
                onClick={() => exitMeetingRoute(navigate, { reason: 'cancelled' })}
                className="w-full h-12 text-sm font-medium text-muted-foreground hover:text-foreground rounded-xl hover:bg-muted/80 transition-all duration-200"
              >
                {t('dashboard.goBack')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
